import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import Button from '../../components/ui/Button'
import ActionPeopleEditor from '../actions/components/ActionPeopleEditor'
import ManagerListPicker from '../managerLists/components/ManagerListPicker'
import type { ManagerList } from '../managerLists/types'
import { personInputToMinutePerson, type PersonInput } from '../actions/types'
import {
  DESCRIPTION_MAX_LENGTH,
  hasDescription,
  nameMaxLength,
  responsibleLabels,
  type MinuteItemKind,
  type MinuteItemRow,
} from './types'

interface Props {
  visible: boolean
  kind: MinuteItemKind
  /** Düzenlenen/eklenen satırın başlangıç değeri (yeni → boş satır). */
  initial: MinuteItemRow
  /** true → yeni ekleme (başlık), false → düzenleme. */
  isNew: boolean
  onSave: (row: MinuteItemRow) => void
  onHide: () => void
}

/**
 * Prensip Kararı / Karar / Gündem Ekle/Düzenle popup'ı (analiz 1.7–1.9). Gündem: Başlık (tek satır) + Açıklama
 * (çok satır); Prensip/Karar: tek metin alanı. Kaydet → zorunlu alanlar doğrulanır, satır döner. Aksiyon
 * dialog'uyla ({@code ActionFormDialog}) aynı desen; skor/termin alanları yoktur.
 */
export default function MinuteItemFormDialog({ visible, kind, initial, isNew, onSave, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const withDescription = hasDescription(kind)
  // Metin sınırı tipe göre: Prensip Kararı 2000, Gündem/Karar 250 (tek kaynak: types.ts).
  const nameMax = nameMaxLength(kind)
  const [draft, setDraft] = useState<MinuteItemRow>(initial)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (visible) {
      setDraft(initial)
      setShowErrors(false)
    }
  }, [visible, initial])

  const invalid = {
    name: draft.name.trim() === '',
    description: withDescription && draft.description.trim() === '',
    // En az bir Sorumlu zorunlu (analiz 1.7–1.9).
    responsibles: draft.responsibles.length === 0,
  }
  const hasError = Object.values(invalid).some(Boolean)

  // Sorumlu ekle/çıkar — sicil bazında tekil (mükerrer ekleme yok sayılır; aksiyon formuyla aynı kalıp).
  const addResponsible = (p: PersonInput) =>
    setDraft((d) =>
      d.responsibles.some((x) => x.employeeId === p.employeeId)
        ? d
        : { ...d, responsibles: [...d.responsibles, personInputToMinutePerson(p)] },
    )
  const removeResponsible = (employeeId: string) =>
    setDraft((d) => ({
      ...d,
      responsibles: d.responsibles.filter((x) => x.employeeId !== employeeId),
    }))

  /**
   * İsim listesiyle toplu ekleme (115) — toplantı detayındaki madde/prensip sorumluları da bu yolu
   * kullanır (kullanıcı isteği 01.10.2026). Kurallar bağımsız prensip kararı ekranıyla aynı: zaten
   * ekli sicil atlanır ve etiketi değişmez; eklenenler listenin etiketini taşır ve gösterimde liste
   * adı altında gruplanır.
   */
  const addList = (ml: ManagerList) =>
    setDraft((d) => {
      const existing = new Set(d.responsibles.map((x) => x.employeeId))
      const added = ml.people
        .filter((p) => !existing.has(p.employeeId))
        .map((p) => ({
          employeeId: p.employeeId,
          fullName: p.fullName || p.employeeId,
          firstName: p.firstName,
          lastName: p.lastName,
          jobTitleTr: p.jobTitleTr,
          jobTitleEn: p.jobTitleEn,
          businessMail: p.businessMail,
          assigneeTypeId: null,
          managerListId: ml.id,
          managerListName: ml.name,
        }))
      return added.length === 0 ? d : { ...d, responsibles: [...d.responsibles, ...added] }
    })

  const submit = () => {
    if (hasError) {
      setShowErrors(true)
      return
    }
    onSave({
      ...draft,
      name: draft.name.trim(),
      description: withDescription ? draft.description.trim() : '',
    })
  }

  const footer = (
    <div className="ui-dialog-footer">
      <Button icon="pi pi-check" label={t('minuteItems.common.save')} onClick={submit} />
    </div>
  )

  return (
    <Dialog
      visible={visible}
      header={t(isNew ? `minuteItems.${kind}.addTitle` : `minuteItems.${kind}.editTitle`)}
      footer={footer}
      onHide={onHide}
      modal
      dismissableMask
      draggable={false}
      // 56rem (kullanıcı isteği 01.10.2026; bağımsız Prensip Kararı pop-up'ıyla aynı ölçü): altta
      // kişi araması + isim listesi seçicisi YAN YANA duruyor ve sorumlu çipleri sarıyor — 38rem'de
      // hepsi sıkışıyordu.
      style={{ width: '56rem', maxWidth: '95vw' }}
    >
      <div className="act-form">
        <label className="act-field act-field--full">
          <span className="ui-field-label">
            {t(`minuteItems.${kind}.name`)} <span className="ui-req">*</span>
          </span>
          {withDescription ? (
            <InputText
              value={draft.name}
              maxLength={nameMax}
              placeholder={t(`minuteItems.${kind}.namePlaceholder`)}
              className={showErrors && invalid.name ? 'p-invalid' : undefined}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          ) : (
            <InputTextarea
              value={draft.name}
              rows={3}
              autoResize
              maxLength={nameMax}
              placeholder={t(`minuteItems.${kind}.namePlaceholder`)}
              className={showErrors && invalid.name ? 'p-invalid' : undefined}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          )}
        </label>

        {withDescription && (
          <label className="act-field act-field--full">
            <span className="ui-field-label">
              {t(`minuteItems.${kind}.description`)} <span className="ui-req">*</span>
            </span>
            <InputTextarea
              value={draft.description}
              rows={3}
              autoResize
              maxLength={DESCRIPTION_MAX_LENGTH}
              className={showErrors && invalid.description ? 'p-invalid' : undefined}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </label>
        )}

        {/* Sorumlu(lar) — çoklu, en az 1 zorunlu; HR aramasından sicil bazında tekil eklenir.
            Alan adı TİPE GÖRE: Gündem'de "Gündem Sahibi" (kullanıcı isteği 2026-09-18). */}
        <div className="act-field act-field--full">
          <span className="ui-field-label">
            {t(responsibleLabels(kind).label)} <span className="ui-req">*</span>
          </span>
          <ActionPeopleEditor
            people={draft.responsibles}
            editable
            onAdd={addResponsible}
            onRemove={removeResponsible}
            placeholder={t('minuteItems.common.responsiblePlaceholder')}
            removeLabel={t('minuteItems.common.remove')}
            searchAddon={
              <ManagerListPicker
                onPick={addList}
                placeholder={t('minuteItems.common.listPlaceholder')}
              />
            }
          />
          {showErrors && invalid.responsibles && (
            <span className="act-form-err">{t(responsibleLabels(kind).required)}</span>
          )}
        </div>

        {showErrors && hasError && (
          <div className="act-form-err">{t('minuteItems.common.invalid')}</div>
        )}
      </div>
    </Dialog>
  )
}
